import { zodResolver } from '@hookform/resolvers/zod';
import { Pencil, Trash2, UserMinus, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useActiveGroup } from '@/features/groups/store';
import { useBalances, useLedgerStore } from '@/features/ledger/store';
import { MemberInUseError } from '@/features/members/repository';
import {
  Avatar,
  Badge,
  Button,
  DataTable,
  EmptyState,
  IconButton,
  Input,
  Modal,
  Money,
  PageHeader,
  balancePhrase,
  confirm,
  toast,
  type Column,
} from '@/ui';
import type { Member } from '@domain/types';

const memberSchema = z.object({
  name: z.string().trim().min(1, 'Enter a name.').max(60, 'Keep it under 60 characters.'),
  phone: z.string().trim().max(30, 'That phone number looks too long.'),
});

type MemberForm = z.infer<typeof memberSchema>;

export function MembersPage() {
  const navigate = useNavigate();

  const group = useActiveGroup();
  const members = useLedgerStore((state) => state.members);
  const addMember = useLedgerStore((state) => state.addMember);
  const editMember = useLedgerStore((state) => state.editMember);
  const removeMember = useLedgerStore((state) => state.removeMember);
  const balances = useBalances();

  const [editing, setEditing] = useState<Member | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const currency = group?.currency ?? 'USD';
  const balanceOf = new Map(balances.map((balance) => [balance.memberId, balance]));

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  async function handleSubmit(values: MemberForm) {
    const payload = { name: values.name, phone: values.phone || null };

    if (editing) {
      await editMember(editing.id, payload);
      toast.success('Member updated.');
    } else {
      await addMember(payload);
      toast.success(`${values.name} added.`);
    }
    setModalOpen(false);
  }

  async function handleDelete(member: Member) {
    const ok = await confirm({
      title: `Remove ${member.name}?`,
      message: 'This cannot be undone.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;

    try {
      await removeMember(member.id);
      toast.success(`${member.name} removed.`);
    } catch (error) {
      if (!(error instanceof MemberInUseError)) {
        toast.error((error as Error).message);
        return;
      }

      const deactivate = await confirm({
        title: `${member.name} is still in use`,
        message:
          `They appear on ${error.usage.paidExpenses} expense${
            error.usage.paidExpenses === 1 ? '' : 's'
          } they paid, ${error.usage.participations} share${
            error.usage.participations === 1 ? '' : 's'
          } and ${error.usage.settlements} settlement${
            error.usage.settlements === 1 ? '' : 's'
          }. Deleting them would rewrite past balances — deactivate them instead to hide them ` +
          'from new expenses while keeping the history intact.',
        confirmLabel: 'Deactivate',
      });
      if (deactivate) {
        await editMember(member.id, { isActive: false });
        toast.success(`${member.name} deactivated.`);
      }
    }
  }

  if (!group) {
    return (
      <>
        <PageHeader title="Members" />
        <EmptyState
          icon={Users}
          title="No group selected"
          description="Choose a group before managing its members."
          action={<Button onClick={() => navigate('/groups')}>Choose a group</Button>}
        />
      </>
    );
  }

  const columns: Column<Member>[] = [
    {
      key: 'name',
      header: 'Member',
      cell: (row) => (
        <span className="flex items-center gap-2.5">
          <Avatar name={row.name} id={row.id} size="sm" dimmed={!row.isActive} />
          <span className="min-w-0">
            <span className="flex items-center gap-2">
              <span className="truncate font-medium">{row.name}</span>
              {!row.isActive && <Badge>Inactive</Badge>}
            </span>
            {row.phone && <span className="block text-[12px] text-muted">{row.phone}</span>}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      hideOnNarrow: true,
      cell: (row) => (
        <span className="text-muted">
          {balancePhrase(balanceOf.get(row.id)?.netMinor ?? 0, currency)}
        </span>
      ),
    },
    {
      key: 'paid',
      header: 'Paid',
      align: 'right',
      hideOnNarrow: true,
      cell: (row) => (
        <Money minor={balanceOf.get(row.id)?.paidMinor ?? 0} currency={currency} muted />
      ),
    },
    {
      key: 'share',
      header: 'Share',
      align: 'right',
      hideOnNarrow: true,
      cell: (row) => (
        <Money minor={balanceOf.get(row.id)?.owedMinor ?? 0} currency={currency} muted />
      ),
    },
    {
      key: 'net',
      header: 'Net',
      align: 'right',
      width: '130px',
      cell: (row) => (
        <Money
          minor={balanceOf.get(row.id)?.netMinor ?? 0}
          currency={currency}
          signed
          className="font-semibold"
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '128px',
      cell: (row) => (
        <span className="flex justify-end gap-0.5" onClick={(event) => event.stopPropagation()}>
          <IconButton
            icon={Pencil}
            label={`Edit ${row.name}`}
            size="sm"
            onClick={() => {
              setEditing(row);
              setModalOpen(true);
            }}
          />
          <IconButton
            icon={row.isActive ? UserMinus : UserPlus}
            label={row.isActive ? `Deactivate ${row.name}` : `Reactivate ${row.name}`}
            size="sm"
            onClick={() => editMember(row.id, { isActive: !row.isActive })}
          />
          <IconButton
            icon={Trash2}
            label={`Remove ${row.name}`}
            size="sm"
            onClick={() => handleDelete(row)}
          />
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Members"
        eyebrow={group.name}
        description={`${members.length} member${members.length === 1 ? '' : 's'}`}
        actions={
          <Button icon={UserPlus} onClick={openCreate}>
            Add member
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={members}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/members/${row.id}`)}
        empty={
          <EmptyState
            icon={UserPlus}
            title="No members yet"
            description={`Add the people who share expenses in ${group.name}.`}
            action={
              <Button icon={UserPlus} onClick={openCreate}>
                Add member
              </Button>
            }
          />
        }
      />

      {members.some((member) => !member.isActive) && (
        <p className="mt-2 text-[12px] text-muted">
          Inactive members keep their history but are hidden when creating new expenses.
        </p>
      )}

      <MemberModal
        key={editing?.id ?? 'new'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        member={editing}
        onSubmit={handleSubmit}
      />
    </>
  );
}

function MemberModal({
  open,
  onClose,
  member,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  member: Member | null;
  onSubmit: (values: MemberForm) => Promise<void>;
}) {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<MemberForm>({
    resolver: zodResolver(memberSchema),
    defaultValues: { name: member?.name ?? '', phone: member?.phone ?? '' },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={member ? 'Edit member' : 'Add member'}
      width="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={isSubmitting} onClick={handleSubmit(onSubmit)}>
            {member ? 'Save changes' : 'Add member'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Input
              label="Name"
              placeholder="e.g. Ayesha"
              error={errors.name?.message}
              autoComplete="off"
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <Input
              label="Phone (optional)"
              placeholder="+44 7700 900000"
              error={errors.phone?.message}
              autoComplete="off"
              {...field}
            />
          )}
        />
      </div>
    </Modal>
  );
}
